/**
 * V2 Earned Media tabs — surfaced above the legacy summary cards when the
 * orchestrator-produced summary is detected (i.e. `recommendations_categorized`
 * or `engine_influence` or `missing_authorities` is present).
 *
 * Renders 5 detail tabs (Médias acquis · Médias à conquérir · Influence
 * moteurs IA · Conversations & forums · Précision) over the same
 * `EarnedMediaAuditSummary` v2 fields. The legacy v1 layout below this
 * component continues to render — it reads the fields shared between v1
 * and v2 (overall score, Wikipedia, E-E-A-T, sources tables, etc.) — so
 * no information is lost on either shape.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddTaskIcon from '@mui/icons-material/AddTask';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type {
  EarnedMediaAuditSummary,
  Recommendation,
  MissingAuthority,
  EngineInfluenceBlock,
  EngineInfluenceDomain,
  EngineInfluenceTrend,
  ConversationMention,
} from '@traaker/shared';

// Match Dashboard COLORS — narrative violet, authority green, referral orange.
const AXIS_COLOR = {
  narrative: '#7C3AED',
  authority: '#0E9F6E',
  referral: '#EA580C',
};

const SEVERITY_COLOR: Record<string, string> = {
  high: '#EF4444',
  medium: '#F59E0B',
  low: '#6B7280',
};

// i18n keys — resolved with `t()` at render (maps live in module scope).
const SEVERITY_LABEL_KEYS: Record<string, string> = {
  high: 'earnedMedia.severityLevel.high',
  medium: 'earnedMedia.severityLevel.medium',
  low: 'earnedMedia.severityLevel.low',
};

// English keys (match DDB / shared types) → i18n keys for French UI labels.
const CLASSIFICATION_LABEL_KEYS: Record<string, string> = {
  media: 'earnedMedia.classification.media',
  public_media: 'earnedMedia.classification.public_media',
  review_platform: 'earnedMedia.classification.review_platform',
  job_board: 'earnedMedia.classification.job_board',
  directory: 'earnedMedia.classification.directory',
  industry_report: 'earnedMedia.classification.industry_report',
  institution: 'earnedMedia.classification.institution',
  association: 'earnedMedia.classification.association',
  educational: 'earnedMedia.classification.educational',
  social: 'earnedMedia.classification.social',
  gov: 'earnedMedia.classification.gov',
  corporate: 'earnedMedia.classification.corporate',
  brand_owned: 'earnedMedia.classification.brand_owned',
  other: 'earnedMedia.classification.other',
};

/** Resolve a classification code to a localized label. `t` is passed since this
 * runs at module scope; unknown codes fall back to the raw code verbatim. */
function classifLabel(t: (k: string) => string, cls?: string): string {
  if (!cls) return t('earnedMedia.classification.other');
  const key = CLASSIFICATION_LABEL_KEYS[cls.toLowerCase()];
  return key ? t(key) : cls;
}

// What "Médias acquis" actually displays. Used by BOTH the tab counter and
// the body filter so they never drift out of sync (which would produce
// "Médias acquis (2)" + "Aucun média acquis" in the same view — a real bug
// we hit on legacy `other`-classified rows).
//
// HIDDEN here means "not earned-media coverage":
//   - brand_owned → that's our own site, not external coverage
//   - social      → routed to Conversations & forums tab instead
//   - other       → classification failure, not a real category
//
// `corporate` is shown — competitor blogs and tool-vendor pages that mention
// the brand ARE earned media (the backend admits them up to 20% of the
// fetch budget). The `entreprise` chip keeps the signal visible so users
// know they're looking at a corporate-class source rather than a press
// outlet.
const HIDDEN_ACQUIRED_CLASSIFICATIONS = new Set([
  'social', 'other', 'brand_owned',
]);

function filterAcquired(sources: any[]): any[] {
  return sources.filter((s) => {
    if ((s.mediaType || 'earned') === 'social') return false;
    const cls = (s.classification || '').toLowerCase();
    if (cls && HIDDEN_ACQUIRED_CLASSIFICATIONS.has(cls)) return false;
    return true;
  });
}

/**
 * Lightweight registered-domain extraction for legacy DDB rows that don't
 * carry a `domain` field. Mirrors the conservative heuristic from the agent
 * code in [_common.registered_domain]: strips scheme + path + leading `www.`,
 * keeps the last 2 (or 3 for known multi-part TLDs) segments. Not a full
 * eTLD parser — adequate for the citation URLs we see in earned-media rows.
 */
function extractRegDomain(url?: string | null): string {
  if (!url || typeof url !== 'string') return '';
  try {
    const raw = url.trim().toLowerCase();
    let host: string;
    if (!raw.includes('://') && !raw.includes('/') && raw.includes('.')) {
      host = raw;
    } else {
      const parsed = new URL(raw.includes('://') ? raw : `https://${raw}`);
      host = parsed.hostname || '';
    }
    if (host.startsWith('www.')) host = host.slice(4);
    const parts = host.split('.');
    if (parts.length < 2) return host;
    const lastTwo = parts.slice(-2).join('.');
    const TWO_PART_TLDS = new Set([
      'co.uk', 'co.jp', 'co.kr', 'co.in', 'co.za', 'co.nz', 'co.id',
      'com.au', 'com.br', 'com.cn', 'com.mx', 'com.tr', 'com.tw',
      'ac.uk', 'gov.uk', 'gov.fr', 'gouv.fr',
    ]);
    if (TWO_PART_TLDS.has(lastTwo) && parts.length >= 3) {
      return parts.slice(-3).join('.');
    }
    return lastTwo;
  } catch {
    return '';
  }
}

// ── Public component ──

export function EarnedMediaV2Tabs({
  summary,
  sources,
  inaccuracies,
  engineInfluence,
  onCreateTask,
}: {
  summary: EarnedMediaAuditSummary;
  sources: any[];
  inaccuracies: any[];
  /**
   * Live, BP-owned engine-influence block (independent of this EM run). When
   * present it OVERRIDES the legacy embedded `summary.engine_influence`. When
   * BOTH are absent the "Influence moteurs IA" tab is hidden entirely (no BP
   * run has populated it yet).
   */
  engineInfluence?: EngineInfluenceBlock | null;
  onCreateTask?: (title: string, severity?: 'high' | 'medium' | 'low') => void;
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);

  // Prefer the live record; fall back to the legacy embedded block (pre-migration audits).
  const influenceBlock = engineInfluence || summary.engine_influence;
  const hasInfluence = !!influenceBlock?.topDomains?.length;

  const counts = useMemo(() => {
    // Use the SAME filter the body applies, so the count and the rendered
    // table can't drift apart.
    const acquiredCount = filterAcquired(sources).length;
    // Missing tab also has a fallback path: if `missing_authorities` is
    // empty, the tab can fall back to legacy coverage_gaps, so the count
    // should reflect what the body actually renders.
    const missingFromV2 = summary.missing_authorities?.length || 0;
    const missingFallback =
      summary.coverage_gap_details?.length || summary.coverage_gaps?.length || 0;
    const missingCount = missingFromV2 || missingFallback;
    const influenceCount = influenceBlock?.topDomains?.length || 0;
    const conversationsCount = summary.conversations?.length || 0;
    const inaccCount = inaccuracies.length;
    return { acquiredCount, missingCount, influenceCount, conversationsCount, inaccCount };
  }, [sources, summary, inaccuracies, influenceBlock]);

  // Build the tab set dynamically so the Influence tab disappears when no BP
  // record exists yet — and reappears once one does, with no index drift.
  const tabs = useMemo(() => {
    const tabList: { key: string; label: string; render: () => ReactNode }[] = [
      {
        key: 'acquired',
        label: t('earnedMedia.tab.acquired', { count: counts.acquiredCount }),
        render: () => (
          <AcquiredTab
            sources={sources}
            inaccuracies={inaccuracies}
            recommendations={summary.recommendations_categorized?.acquired_media || []}
            wikipediaScore={summary.wikipedia_quality_score}
            wikipediaPresence={summary.wikipedia_presence}
            wikipediaEnvergure={summary.wikipedia_envergure_context}
            onCreateTask={onCreateTask}
          />
        ),
      },
      {
        key: 'missing',
        label: t('earnedMedia.tab.missing', { count: counts.missingCount }),
        render: () => (
          <MissingTab
            missing={summary.missing_authorities || []}
            coverageGapDetails={summary.coverage_gap_details || []}
            coverageGaps={summary.coverage_gaps || []}
            recommendations={summary.recommendations_categorized?.missing_media || []}
            onCreateTask={onCreateTask}
          />
        ),
      },
    ];
    if (hasInfluence) {
      tabList.push({
        key: 'influence',
        label: t('earnedMedia.tab.influence', { count: counts.influenceCount }),
        render: () => <InfluenceTab block={influenceBlock} sources={sources} />,
      });
    }
    tabList.push({
      key: 'conversations',
      label: t('earnedMedia.tab.conversations', { count: counts.conversationsCount }),
      render: () => <ConversationsTab conversations={summary.conversations || []} />,
    });
    tabList.push({
      key: 'precision',
      label: t('earnedMedia.tab.precision', { count: counts.inaccCount }),
      render: () => (
        <PrecisionTab
          inaccuracies={inaccuracies}
          recommendations={summary.recommendations_categorized?.precision || []}
          onCreateTask={onCreateTask}
        />
      ),
    });
    return tabList;
  }, [counts, hasInfluence, influenceBlock, sources, inaccuracies, summary, onCreateTask, t]);

  // Guard against a stale index when the tab set shrinks (Influence removed).
  const activeTab = Math.min(tab, tabs.length - 1);

  return (
    <Card sx={{ mb: 3 }}>
      <Tabs
        value={activeTab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
      >
        {tabs.map((t) => (
          <Tab key={t.key} label={t.label} />
        ))}
      </Tabs>
      <CardContent sx={{ p: 3 }}>{tabs[activeTab]?.render()}</CardContent>
    </Card>
  );
}

// ── Acquired tab ──

function AcquiredTab({
  sources,
  inaccuracies,
  recommendations,
  wikipediaScore,
  wikipediaPresence,
  wikipediaEnvergure,
  onCreateTask,
}: {
  sources: any[];
  inaccuracies: any[];
  recommendations: Recommendation[];
  wikipediaScore?: number;
  wikipediaPresence?: string;
  wikipediaEnvergure?: string;
  onCreateTask?: (t: string, s?: 'high' | 'medium' | 'low') => void;
}) {
  const { t } = useTranslation();
  // Acquired = editorial press / blogs / directories / review platforms.
  // Defined once at module-scope (filterAcquired) to keep the tab counter
  // and the body in sync. Hides:
  //  - social (forums, Reddit threads — they belong in Conversations tab)
  //  - `other` / `gov` / `brand_owned` (not editorial coverage —
  //    excluded by the orchestrator, but we keep the client-side guard
  //    for back-compat with pre-2026-05-22 audits that were less strict)
  const acquired = filterAcquired(sources);

  // Index inaccuracies by registered domain so we can surface "X has Y issues"
  // directly inside the domain row instead of leaving a count badge.
  // Back-compat: pre-2026-05-22 inaccuracy rows lack the `domain` field
  // (only `sourceUrl` / `sourceName`). We derive a registered domain from
  // the URL or fall back to sourceName so old audits still display the
  // expand caret + inline issue list.
  const inaccByDomain = useMemo(() => {
    const m = new Map<string, any[]>();
    for (const i of inaccuracies || []) {
      const key = ((i.domain as string | undefined) || extractRegDomain(i.sourceUrl) || (i.sourceName || '')).toLowerCase();
      if (!key) continue;
      const list = m.get(key) || [];
      list.push(i);
      m.set(key, list);
    }
    return m;
  }, [inaccuracies]);

  // Empty state: no acquired sources yet. If the synthesizer produced
  // recommendations we surface them; otherwise we steer the user toward
  // the "Médias à conquérir" tab (which usually has actionable rows even
  // when the acquired list is empty).
  if (!acquired.length) {
    const hasRecs = recommendations.length > 0;
    return (
      <Box>
        <CalloutInfo>
          📚 <Trans i18nKey="earnedMedia.acquired.calloutEmpty" components={{ strong: <strong /> }} />
        </CalloutInfo>
        <WikipediaScoreCard
          score={wikipediaScore}
          presence={wikipediaPresence}
          envergure={wikipediaEnvergure}
        />
        <Box sx={{ mt: 2 }}>
          <EmptyState
            text={t('earnedMedia.acquired.emptyText')}
            sub={
              hasRecs
                ? t('earnedMedia.acquired.emptySubWithRecs')
                : t('earnedMedia.acquired.emptySubNoRecs')
            }
          />
        </Box>
        {hasRecs && (
          <RecommendationList
            title={t('earnedMedia.acquired.recsTitle')}
            recs={recommendations}
            onCreateTask={onCreateTask}
          />
        )}
      </Box>
    );
  }

  return (
    <Box>
      <CalloutInfo>
        📚 <Trans i18nKey="earnedMedia.acquired.callout" components={{ strong: <strong /> }} />
      </CalloutInfo>

      <WikipediaScoreCard
        score={wikipediaScore}
        presence={wikipediaPresence}
        envergure={wikipediaEnvergure}
      />

      <TableContainer sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('earnedMedia.col.domain')}</TableCell>
              <TableCell>{t('earnedMedia.col.category')}</TableCell>
              <TableCell>{t('earnedMedia.col.sentiment')}</TableCell>
              <TableCell>{t('earnedMedia.col.precision')}</TableCell>
              <TableCell>{t('earnedMedia.col.recency')}</TableCell>
              <TableCell align="right">{t('earnedMedia.col.score')}</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupByDomain(acquired).map((grp, gi) => {
              const inaccs = inaccByDomain.get(grp.key) || [];
              // Single-page domain → flat row (unchanged behavior). Multi-page
              // domain → expandable group header showing every scored page
              // (the multi-page-per-domain volume from Serper num=100 harvest).
              if (grp.pages.length === 1) {
                return (
                  <AcquiredRow
                    key={`${grp.key || gi}-single`}
                    source={grp.pages[0]}
                    inaccs={inaccs}
                    onCreateTask={onCreateTask}
                  />
                );
              }
              return (
                <DomainGroupRows
                  key={`${grp.key || gi}-group`}
                  group={grp}
                  inaccs={inaccs}
                  onCreateTask={onCreateTask}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {recommendations.length > 0 && (
        <RecommendationList
          title={t('earnedMedia.acquired.recsTitle')}
          recs={recommendations}
          onCreateTask={onCreateTask}
        />
      )}
    </Box>
  );
}

/** Short, readable page label from a URL — the path (+ query), truncated.
 * Falls back to the full URL, then "(page)". Used for per-page rows under a
 * domain group header where the domain is already shown. */
function pagePathLabel(url?: string): string {
  if (!url) return '';
  try {
    const u = new URL(url);
    const path = (u.pathname + u.search).replace(/\/$/, '') || '/';
    return path.length > 60 ? path.slice(0, 57) + '…' : path;
  } catch {
    return url.length > 60 ? url.slice(0, 57) + '…' : url;
  }
}

type DomainGroup = { key: string; domain: string; pages: any[]; bestScore: number };

/** Group per-URL source rows by registered domain. With multi-page-per-domain
 * (Serper num=100 harvest), one media can yield many scored pages; we collapse
 * them under a single domain header. Order preserved by best page score. */
function groupByDomain(sources: any[]): DomainGroup[] {
  const map = new Map<string, DomainGroup>();
  for (const s of sources) {
    const key = (
      (s.domain as string | undefined) ||
      extractRegDomain(s.sourceUrl) ||
      (s.sourceName || '')
    ).toLowerCase();
    const existing = map.get(key);
    const score = s.relevanceScore ?? 0;
    if (existing) {
      existing.pages.push(s);
      existing.bestScore = Math.max(existing.bestScore, score);
    } else {
      map.set(key, { key, domain: s.domain || s.sourceName || key || '?', pages: [s], bestScore: score });
    }
  }
  const groups = Array.from(map.values());
  // Sort each domain's pages by score desc, and the groups by best score desc.
  for (const g of groups) g.pages.sort((a, b) => (b.relevanceScore ?? 0) - (a.relevanceScore ?? 0));
  groups.sort((a, b) => b.bestScore - a.bestScore);
  return groups;
}

/** Expandable header row for a domain with multiple judged pages. Collapsed by
 * default: shows domain + page count + best score. Expands to a per-page list,
 * each page rendered as a normal AcquiredRow. */
function DomainGroupRows({
  group,
  inaccs,
  onCreateTask,
}: {
  group: DomainGroup;
  inaccs: any[];
  onCreateTask?: (t: string, sev?: 'high' | 'medium' | 'low') => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const sentiments = new Set(group.pages.map((p) => p.sentiment || 'neutral'));
  const sentimentLabel = sentiments.size === 1 ? [...sentiments][0] : 'mixte';
  return (
    <>
      <TableRow hover sx={{ cursor: 'pointer', bgcolor: '#FAFBFC' }} onClick={() => setOpen((v) => !v)}>
        <TableCell sx={{ fontWeight: 700 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ fontSize: 11, color: '#6B7280' }}>{open ? '▾' : '▸'}</Box>
            <span>{group.domain}</span>
            <Chip
              label={t('earnedMedia.pagesCount', { count: group.pages.length })}
              size="small"
              sx={{ bgcolor: '#EEF2FF', color: '#4338CA', fontWeight: 600, height: 20 }}
            />
          </Stack>
        </TableCell>
        <TableCell>
          <Chip label={classifLabel(t, group.pages[0].classification || group.pages[0].sourceType)} size="small" variant="outlined" />
        </TableCell>
        <TableCell>
          <Chip
            label={t(`earnedMedia.sentiment.${sentimentLabel}`)}
            size="small"
            sx={{
              bgcolor:
                sentimentLabel === 'positive' ? '#ECFDF5' : sentimentLabel === 'negative' ? '#FEF2F2' : '#EEF1F5',
            }}
          />
        </TableCell>
        <TableCell>
          {inaccs.length > 0 ? (
            <Chip
              label={t('earnedMedia.toFix', { count: inaccs.length })}
              size="small"
              sx={{ bgcolor: '#FEF2F2', color: '#EF4444', fontWeight: 600 }}
            />
          ) : (
            <Typography variant="caption" sx={{ color: '#9CA3AF' }}>—</Typography>
          )}
        </TableCell>
        <TableCell>—</TableCell>
        <TableCell align="right" sx={{ fontWeight: 700 }}>
          {group.bestScore}
        </TableCell>
        <TableCell />
      </TableRow>
      {open &&
        group.pages.map((p, i) => (
          <AcquiredRow
            key={`${group.key}-${p.SK || i}`}
            source={p}
            inaccs={inaccs.filter((x) => (x.sourceUrl || '') === (p.sourceUrl || ''))}
            onCreateTask={onCreateTask}
            nested
          />
        ))}
    </>
  );
}

function AcquiredRow({
  source: s,
  inaccs,
  onCreateTask,
  nested = false,
}: {
  source: any;
  inaccs: any[];
  onCreateTask?: (t: string, sev?: 'high' | 'medium' | 'low') => void;
  nested?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const hasInaccs = inaccs.length > 0;
  const domain = s.domain || s.sourceName || '?';
  // Three precision states:
  //  - exact         : Haiku confirmed brandAccurate=true, no logged inaccuracies.
  //  - detailed      : N inaccuracies logged → expand row to see claim/réalité.
  //  - flagged-only  : Haiku set brandAccurate=false but didn't enumerate anything
  //                    (rare but happens). Chip shows "à vérifier" with no caret —
  //                    avoids promising details we don't have.
  const flaggedNoDetail = !hasInaccs && s.brandAccurate === false;
  const accurate = !hasInaccs && !flaggedNoDetail;

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: hasInaccs ? 'pointer' : 'default', '& > td': { borderBottom: hasInaccs && open ? 0 : undefined } }}
        onClick={() => hasInaccs && setOpen((v) => !v)}
      >
        <TableCell sx={{ fontWeight: nested ? 400 : 600, pl: nested ? 4 : undefined }}>
          <Stack direction="row" spacing={1} alignItems="center">
            {hasInaccs && (
              <Box sx={{ fontSize: 11, color: '#EF4444' }}>{open ? '▾' : '▸'}</Box>
            )}
            {/* Under a domain group, the header already shows the domain — show
                the page path here so each scored page is identifiable. */}
            <span style={nested ? { color: '#4B5563', fontSize: 13 } : undefined}>
              {nested ? pagePathLabel(s.sourceUrl) || domain : domain}
            </span>
          </Stack>
        </TableCell>
        <TableCell>
          <Chip label={classifLabel(t, s.classification || s.sourceType)} size="small" variant="outlined" />
        </TableCell>
        <TableCell>
          <Chip
            label={t(`earnedMedia.sentiment.${s.sentiment || 'neutral'}`)}
            size="small"
            sx={{
              bgcolor:
                s.sentiment === 'positive' ? '#ECFDF5' : s.sentiment === 'negative' ? '#FEF2F2' : '#EEF1F5',
            }}
          />
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
            {accurate && s.fetchStatus !== 'blocked' && (
              <Chip label={t('earnedMedia.precision.exact')} size="small" sx={{ bgcolor: '#ECFDF5', color: '#0E9F6E' }} />
            )}
            {hasInaccs && (
              <Chip
                label={t('earnedMedia.toFix', { count: inaccs.length })}
                size="small"
                sx={{ bgcolor: '#FEF2F2', color: '#EF4444', fontWeight: 600 }}
              />
            )}
            {flaggedNoDetail && (
              <Tooltip title={t('earnedMedia.precision.toVerifyTooltip')}>
                <Chip
                  label={t('earnedMedia.precision.toVerify')}
                  size="small"
                  sx={{ bgcolor: '#FFFBEB', color: '#B45309', fontWeight: 600 }}
                />
              </Tooltip>
            )}
            {/* "lecture partielle" — page was blocked by anti-bot/paywall;
                we know the brand is mentioned (Tavily snippet passed the
                literal-name guard) but couldn't read the full page to
                judge accuracy/sentiment. */}
            {s.fetchStatus === 'blocked' && (
              <Tooltip title={t('earnedMedia.precision.partialReadTooltip')}>
                <Chip
                  label={t('earnedMedia.precision.partialRead')}
                  size="small"
                  sx={{ bgcolor: '#F3F4F6', color: '#6B7280', fontWeight: 600 }}
                />
              </Tooltip>
            )}
            {/* "mention seulement" — the brand is referenced on the page but
                isn't its primary subject (typical: directory/listing/comparatif).
                Default true on legacy rows so they don't all light up. */}
            {s.brandPrimarySubject === false && (
              <Tooltip title={t('earnedMedia.precision.mentionOnlyTooltip')}>
                <Chip
                  label={t('earnedMedia.precision.mentionOnly')}
                  size="small"
                  variant="outlined"
                  sx={{ borderColor: '#D1D5DB', color: '#6B7280' }}
                />
              </Tooltip>
            )}
          </Stack>
        </TableCell>
        <TableCell>{s.recency || '—'}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 600 }}>
          {s.relevanceScore ?? 0}
        </TableCell>
        <TableCell align="right">
          {s.sourceUrl && (
            <IconButton
              size="small"
              component="a"
              href={s.sourceUrl}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          )}
        </TableCell>
      </TableRow>

      {hasInaccs && open && (
        <TableRow>
          <TableCell colSpan={7} sx={{ bgcolor: '#FFFAF6', p: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#991B1B', display: 'block', mb: 1 }}>
              ⚠ <Trans i18nKey="earnedMedia.inaccExpand.header" count={inaccs.length} values={{ count: inaccs.length }} components={{ strong: <strong /> }} />
            </Typography>
            <Stack spacing={1}>
              {inaccs.map((i, idx) => (
                <Box
                  key={`${i.SK || idx}`}
                  sx={{
                    p: 1.5,
                    bgcolor: 'white',
                    border: 1,
                    borderColor: i.severity === 'critical' ? '#FECACA' : '#FED7AA',
                    borderRadius: 1,
                    fontSize: 12.5,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Chip
                      label={i.severity === 'critical' ? t('earnedMedia.severity.criticalFem') : t('earnedMedia.severity.minorFem')}
                      size="small"
                      sx={{
                        bgcolor: i.severity === 'critical' ? '#FEF2F2' : '#FFFBEB',
                        color: i.severity === 'critical' ? '#EF4444' : '#F59E0B',
                        fontWeight: 600,
                        fontSize: 10,
                      }}
                    />
                    {i.sourceUrl && (
                      <Typography variant="caption" component="a" href={i.sourceUrl} target="_blank" rel="noreferrer" sx={{ color: '#2563EB', textDecoration: 'none' }}>
                        {t('earnedMedia.articleLink')}
                      </Typography>
                    )}
                  </Stack>
                  <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                    <Box sx={{ p: 0.75, px: 1, bgcolor: '#FEF2F2', color: '#991B1B', borderRadius: 0.5, flex: 1 }}>
                      « {i.claim} »
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Box sx={{ p: 0.75, px: 1, bgcolor: '#ECFDF5', color: '#065F46', borderRadius: 0.5, flex: 1 }}>
                      {i.reality}
                    </Box>
                  </Box>
                  {onCreateTask && (
                    <Button
                      size="small"
                      startIcon={<AddTaskIcon fontSize="small" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onCreateTask(
                          t('earnedMedia.correctInaccuracyTask', { source: i.sourceName || i.domain, claim: i.claim }),
                          i.severity === 'critical' ? 'high' : 'medium',
                        );
                      }}
                      sx={{ mt: 1 }}
                    >
                      {t('earnedMedia.createCorrectiveTask')}
                    </Button>
                  )}
                </Box>
              ))}
            </Stack>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// ── Missing tab ──

function MissingTab({
  missing,
  coverageGapDetails,
  coverageGaps,
  recommendations,
  onCreateTask,
}: {
  missing: MissingAuthority[];
  coverageGapDetails: { domain: string; category?: string; rationale?: string }[];
  coverageGaps: string[];
  recommendations: Recommendation[];
  onCreateTask?: (t: string, s?: 'high' | 'medium' | 'low') => void;
}) {
  const { t } = useTranslation();
  // KPI strip metrics
  const totalLeverage = missing.reduce((acc, m) => acc + (m.leverageScore || 0), 0);
  const avgLeverage = missing.length ? Math.round(totalLeverage / missing.length) : 0;
  const citedByEngines = missing.filter(
    (m) =>
      m.citedByEngines &&
      (m.citedByEngines.narrative + m.citedByEngines.authority + m.citedByEngines.referral) > 0,
  ).length;

  // Fallback: if synthesizer didn't produce missing_authorities but the legacy
  // coverage_gaps array is populated, surface those so the tab isn't empty.
  const fallbackEntries: MissingAuthority[] = useMemo(() => {
    if (missing.length > 0) return [];
    const fromDetails = (coverageGapDetails || []).map((g) => ({
      domain: g.domain,
      category: g.category || 'trade_press',
      rationale: g.rationale || '',
      leverageScore: 50,
      citedByEngines: null,
      competitorsCovered: [],
      suggestedAngle: '',
      effort: 'medium' as const,
    }));
    if (fromDetails.length > 0) return fromDetails;
    return (coverageGaps || []).map((d) => ({
      domain: d,
      category: 'trade_press',
      rationale: '',
      leverageScore: 50,
      citedByEngines: null,
      competitorsCovered: [],
      suggestedAngle: '',
      effort: 'medium' as const,
    }));
  }, [missing.length, coverageGapDetails, coverageGaps]);

  const rows = missing.length > 0 ? missing : fallbackEntries;

  if (!rows.length && !recommendations.length) {
    return <EmptyState text={t('earnedMedia.missing.empty')} />;
  }

  return (
    <Box>
      <CalloutInfo>
        🎯 <Trans i18nKey="earnedMedia.missing.callout" components={{ strong: <strong /> }} />
      </CalloutInfo>

      <Stack
        direction="row"
        spacing={2}
        sx={{
          mt: 2,
          mb: 1,
          flexWrap: 'wrap',
          '& > *': { minWidth: 140, flex: '1 1 0' },
        }}
      >
        <KpiTile label={t('earnedMedia.missing.kpiToConquer')} value={`${rows.length}`} />
        <KpiTile
          label={t('earnedMedia.missing.kpiAvgLeverage')}
          value={t('earnedMedia.missing.pts', { value: avgLeverage })}
          accent={avgLeverage >= 60 ? '#0E9F6E' : avgLeverage >= 30 ? '#F59E0B' : '#6B7280'}
        />
        <KpiTile
          label={t('earnedMedia.missing.kpiCitedByEngines')}
          value={`${citedByEngines} / ${rows.length}`}
          hint={citedByEngines > 0 ? t('earnedMedia.missing.strongContext') : ''}
        />
        <KpiTile
          label={t('earnedMedia.missing.kpiQuickWins')}
          value={`${rows.filter((m) => m.effort === 'low').length}`}
        />
      </Stack>

      <TableContainer sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('earnedMedia.col.domain')}</TableCell>
              <TableCell>{t('earnedMedia.col.category')}</TableCell>
              <TableCell>{t('earnedMedia.missing.colLeverage')}</TableCell>
              <Tooltip
                title={t('earnedMedia.missing.coCitedTooltip')}
                placement="top"
                arrow
              >
                <TableCell sx={{ cursor: 'help', textDecoration: 'underline dotted' }}>
                  {t('earnedMedia.missing.colCoCited')}
                </TableCell>
              </Tooltip>
              <TableCell>{t('earnedMedia.missing.colSuggestedAngle')}</TableCell>
              <TableCell>{t('earnedMedia.missing.colEffort')}</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((m) => (
              <TableRow key={m.domain}>
                <TableCell sx={{ fontWeight: 600 }}>{m.domain}</TableCell>
                <TableCell>
                  <Chip label={m.category} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  <Chip
                    label={t('earnedMedia.missing.pts', { value: m.leverageScore })}
                    size="small"
                    sx={{
                      bgcolor: m.leverageScore >= 70 ? '#ECFDF5' : m.leverageScore >= 40 ? '#FFFBEB' : '#FEF2F2',
                      color: m.leverageScore >= 70 ? '#0E9F6E' : m.leverageScore >= 40 ? '#F59E0B' : '#EF4444',
                      fontWeight: 600,
                    }}
                  />
                </TableCell>
                <TableCell>
                  {m.competitorsCovered?.length ? (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {m.competitorsCovered.slice(0, 3).map((c) => (
                        <Chip key={c} label={c} size="small" sx={{ fontSize: 11 }} />
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="caption" color="text.secondary">—</Typography>
                  )}
                </TableCell>
                <TableCell sx={{ maxWidth: 280 }}>
                  <Typography variant="caption">{m.suggestedAngle || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={m.effort === 'low' ? t('earnedMedia.effort.low') : m.effort === 'high' ? t('earnedMedia.effort.high') : t('earnedMedia.effort.medium')}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  {onCreateTask && m.suggestedAngle && (
                    <Tooltip title={t('earnedMedia.missing.createTaskTooltip')}>
                      <IconButton
                        size="small"
                        onClick={() =>
                          onCreateTask(`${m.domain} — ${m.suggestedAngle}`, 'medium')
                        }
                      >
                        <AddTaskIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {recommendations.length > 0 && (
        <RecommendationList
          title={t('earnedMedia.missing.recsTitle')}
          recs={recommendations}
          onCreateTask={onCreateTask}
        />
      )}
    </Box>
  );
}

// ── Influence tab ──

type InfluenceSortKey = 'total' | 'narrative' | 'authority' | 'referral' | 'domain';
type InfluenceFilter = 'all' | 'acquired' | 'missing';

function InfluenceTab({
  block,
  sources,
}: {
  block?: EarnedMediaAuditSummary['engine_influence'];
  sources: any[];
}) {
  const { t } = useTranslation();
  const [sortKey, setSortKey] = useState<InfluenceSortKey>('total');
  const [sortDesc, setSortDesc] = useState(true);
  const [filter, setFilter] = useState<InfluenceFilter>('all');
  // null = all classifications visible. A Set means "show only these classifications".
  const [classificationFilter, setClassificationFilter] = useState<Set<string> | null>(null);
  const [showExcluded, setShowExcluded] = useState(false);

  if (!block || !block.topDomains?.length) {
    return (
      <EmptyState
        text={t('earnedMedia.influence.emptyText')}
        sub={t('earnedMedia.influence.emptySub')}
      />
    );
  }

  const acquiredDomains = new Set(sources.map((s) => s.domain).filter(Boolean));
  const trendsByDomain = new Map<string, EngineInfluenceTrend>();
  for (const t of block.domainTrends || []) trendsByDomain.set(t.domain, t);

  // Build classification counts so the chip strip shows live counts.
  const classificationCounts = new Map<string, number>();
  for (const d of block.topDomains) {
    const cls = (d.classification || 'other').toLowerCase();
    classificationCounts.set(cls, (classificationCounts.get(cls) || 0) + 1);
  }

  // Filter + sort. `isAcquired === null` means "not measured" (Earned Media
  // structurally never fetches this domain, e.g. YouTube/LinkedIn) — it's
  // neither "acquired" nor "missing", so it's excluded from both filters.
  const filtered = block.topDomains.filter((d) => {
    if (d.isAcquired === null) {
      if (filter === 'acquired' || filter === 'missing') return false;
    } else {
      const acquired = d.isAcquired || acquiredDomains.has(d.domain);
      if (filter === 'acquired' && !acquired) return false;
      if (filter === 'missing' && acquired) return false;
    }
    if (classificationFilter && classificationFilter.size > 0) {
      const cls = (d.classification || 'other').toLowerCase();
      if (!classificationFilter.has(cls)) return false;
    }
    return true;
  });
  const sorted = [...filtered].sort((a, b) => {
    let av: string | number;
    let bv: string | number;
    if (sortKey === 'domain') {
      av = a.domain;
      bv = b.domain;
    } else {
      av = a[sortKey] || 0;
      bv = b[sortKey] || 0;
    }
    if (av === bv) return 0;
    const cmp = av > bv ? 1 : -1;
    return sortDesc ? -cmp : cmp;
  });

  const setSort = (key: InfluenceSortKey) => {
    if (sortKey === key) {
      setSortDesc(!sortDesc);
    } else {
      setSortKey(key);
      setSortDesc(true);
    }
  };

  const SortHeader = ({ label, k, align = 'left' }: { label: string; k: InfluenceSortKey; align?: 'left' | 'right' }) => (
    <TableCell
      align={align}
      sx={{ cursor: 'pointer', userSelect: 'none', '&:hover': { color: 'primary.main' } }}
      onClick={() => setSort(k)}
    >
      {label}
      {sortKey === k && <Box component="span" sx={{ ml: 0.5, fontSize: 10 }}>{sortDesc ? '▾' : '▴'}</Box>}
    </TableCell>
  );

  const unmeasuredCount = block.topDomains.filter((d) => d.isAcquired === null).length;
  const acquiredCount = block.topDomains.filter((d) => d.isAcquired !== null && (d.isAcquired || acquiredDomains.has(d.domain))).length;
  const missingCount = block.topDomains.length - acquiredCount - unmeasuredCount;

  return (
    <Box>
      <CalloutInfo>
        🔭 <Trans i18nKey="earnedMedia.influence.callout" components={{ strong: <strong /> }} />
      </CalloutInfo>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2, mb: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', gap: 4, color: 'text.secondary', fontSize: 12 }}>
          <span>
            <Trans i18nKey="earnedMedia.influence.cyclesAggregated" count={block.cyclesObserved} values={{ count: block.cyclesObserved }} components={{ strong: <strong /> }} />
          </span>
          <span>
            {t('earnedMedia.influence.window', { start: block.windowStart || '—', end: block.windowEnd || '—' })}
          </span>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Stack direction="row" spacing={0.5}>
          <FilterChip label={t('earnedMedia.influence.filterAll', { count: block.topDomains.length })} active={filter === 'all'} onClick={() => setFilter('all')} />
          <FilterChip label={t('earnedMedia.influence.filterAcquired', { count: acquiredCount })} active={filter === 'acquired'} onClick={() => setFilter('acquired')} />
          <FilterChip label={t('earnedMedia.influence.filterMissing', { count: missingCount })} active={filter === 'missing'} onClick={() => setFilter('missing')} />
        </Stack>
      </Stack>

      {/* Classification multi-select. Shown only when ≥ 2 categories are
          present — single-category audits don't need the chip strip. */}
      {classificationCounts.size > 1 && (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{ mb: 2, flexWrap: 'wrap', rowGap: 0.5 }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ mr: 1, fontWeight: 600 }}>
            {t('earnedMedia.influence.typeLabel')}
          </Typography>
          <FilterChip
            label={t('earnedMedia.influence.typeAll')}
            active={classificationFilter === null || classificationFilter.size === 0}
            onClick={() => setClassificationFilter(null)}
          />
          {Array.from(classificationCounts.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([cls, count]) => {
              const active = !!classificationFilter?.has(cls);
              return (
                <FilterChip
                  key={cls}
                  label={`${classifLabel(t, cls)} (${count})`}
                  active={active}
                  onClick={() => {
                    setClassificationFilter((prev) => {
                      const next = new Set(prev || []);
                      if (next.has(cls)) {
                        next.delete(cls);
                      } else {
                        next.add(cls);
                      }
                      return next.size === 0 ? null : next;
                    });
                  }}
                />
              );
            })}
        </Stack>
      )}

      <TableContainer sx={{ mt: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortHeader label={t('earnedMedia.col.domain')} k="domain" />
              <TableCell>{t('earnedMedia.col.category')}</TableCell>
              <SortHeader label={t('earnedMedia.influence.colNarrative')} k="narrative" align="right" />
              <SortHeader label={t('earnedMedia.influence.colAuthority')} k="authority" align="right" />
              <SortHeader label={t('earnedMedia.influence.colReferral')} k="referral" align="right" />
              <TableCell sx={{ minWidth: 110 }}>{t('earnedMedia.influence.colDistribution')}</TableCell>
              <SortHeader label={t('earnedMedia.influence.colTotal')} k="total" align="right" />
              <TableCell sx={{ minWidth: 110 }}>{t('earnedMedia.influence.colTrend')}</TableCell>
              <TableCell>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <span>{t('earnedMedia.influence.colAcquired')}</span>
                  <Tooltip title={t('earnedMedia.influence.colAcquiredInfo')} arrow placement="top">
                    <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled', cursor: 'help' }} />
                  </Tooltip>
                </Stack>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((d) => (
              <TableRow key={d.domain}>
                <TableCell sx={{ fontWeight: 600 }}>{d.domain}</TableCell>
                <TableCell>
                  <Chip label={classifLabel(t, d.classification)} size="small" variant="outlined" />
                </TableCell>
                <TableCell align="right">{d.narrative}</TableCell>
                <TableCell align="right">{d.authority}</TableCell>
                <TableCell align="right">{d.referral}</TableCell>
                <TableCell>
                  <DistributionBar d={d} />
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {d.total}
                </TableCell>
                <TableCell>
                  <Sparkline trend={trendsByDomain.get(d.domain)} />
                </TableCell>
                <TableCell>
                  {d.isAcquired === null ? (
                    <Tooltip title={t('earnedMedia.influence.notMeasuredInfo')} arrow placement="top">
                      <Chip label={t('earnedMedia.influence.notMeasuredChip')} size="small" sx={{ bgcolor: '#F3F4F6', color: '#6B7280', cursor: 'help' }} />
                    </Tooltip>
                  ) : d.isAcquired || acquiredDomains.has(d.domain) ? (
                    <Chip label={t('earnedMedia.influence.acquiredChip')} size="small" sx={{ bgcolor: '#ECFDF5', color: '#0E9F6E' }} />
                  ) : (
                    <Chip label={t('earnedMedia.influence.toConquerChip')} size="small" sx={{ bgcolor: '#FFFBEB', color: '#F59E0B' }} />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {block.excludedDomains && block.excludedDomains.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Button
            size="small"
            onClick={() => setShowExcluded((v) => !v)}
            sx={{ color: 'text.secondary', fontSize: 12 }}
          >
            {showExcluded ? '▾' : '▸'} {t('earnedMedia.influence.excludedToggle', { count: block.excludedDomains.length })}
          </Button>
          {showExcluded && (
            <Box sx={{ mt: 1, p: 2, bgcolor: '#FAFBFC', borderRadius: 1, border: 1, borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {t('earnedMedia.influence.excludedCaption')}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {block.excludedDomains.map((e) => (
                  <Chip
                    key={e.domain}
                    label={t('earnedMedia.influence.excludedChip', { domain: e.domain, classification: classifLabel(t, e.classification), total: e.total })}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: 11 }}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

function FilterChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <Chip
      label={label}
      size="small"
      onClick={onClick}
      sx={{
        bgcolor: active ? '#7C3AED' : '#EEF1F5',
        color: active ? 'white' : 'text.secondary',
        fontWeight: active ? 600 : 500,
        cursor: 'pointer',
        '&:hover': { bgcolor: active ? '#6D28D9' : '#E2E8F0' },
      }}
    />
  );
}

function DistributionBar({ d }: { d: EngineInfluenceDomain }) {
  const total = d.total || 1;
  const seg = (n: number, color: string) => (
    <Box sx={{ width: `${(n / total) * 100}%`, height: 8, bgcolor: color }} />
  );
  return (
    <Box sx={{ display: 'flex', height: 8, borderRadius: 1, overflow: 'hidden', bgcolor: '#F1F3F7' }}>
      {seg(d.narrative, AXIS_COLOR.narrative)}
      {seg(d.authority, AXIS_COLOR.authority)}
      {seg(d.referral, AXIS_COLOR.referral)}
    </Box>
  );
}

function Sparkline({ trend }: { trend?: EngineInfluenceTrend }) {
  if (!trend || trend.monthly.length < 2) {
    return <Typography variant="caption" color="text.secondary">—</Typography>;
  }
  const max = Math.max(...trend.monthly.map((m) => m.count), 1);
  const w = 100;
  const h = 22;
  const stepX = w / Math.max(trend.monthly.length - 1, 1);
  const points = trend.monthly
    .map((m, i) => `${i * stepX},${h - (m.count / max) * (h - 2) - 1}`)
    .join(' ');
  return (
    <svg width={w} height={h}>
      <polyline points={points} fill="none" stroke="#7C3AED" strokeWidth={1.5} />
    </svg>
  );
}

// ── Conversations tab ──

function ConversationsTab({ conversations }: { conversations: ConversationMention[] }) {
  const { t } = useTranslation();
  if (!conversations.length) {
    return <EmptyState text={t('earnedMedia.conversations.empty')} />;
  }
  return (
    <Box>
      <CalloutInfo>
        💬 <Trans i18nKey="earnedMedia.conversations.callout" components={{ strong: <strong /> }} />
      </CalloutInfo>

      <Stack spacing={2} sx={{ mt: 2 }}>
        {conversations.map((c, i) => (
          <Card key={`${c.platform}-${i}`} variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={c.platform}
                  size="small"
                  sx={{
                    bgcolor: c.platform === 'reddit' ? '#FEEFE6' : c.platform === 'hackernews' ? '#FFEAE0' : '#EEF1F5',
                    color: '#15171C',
                    fontWeight: 600,
                  }}
                />
                <Chip
                  label={
                    c.sentiment === 'positive' ? t('earnedMedia.conversations.positive') : c.sentiment === 'negative' ? t('earnedMedia.conversations.negative') : t('earnedMedia.conversations.neutral')
                  }
                  size="small"
                  variant="outlined"
                />
                {c.engagement?.comments != null && (
                  <Typography variant="caption" color="text.secondary">
                    {t('earnedMedia.conversations.comments', { count: c.engagement.comments })}
                  </Typography>
                )}
                {c.engagement?.upvotes != null && (
                  <Typography variant="caption" color="text.secondary">
                    {t('earnedMedia.conversations.upvotes', { count: c.engagement.upvotes })}
                  </Typography>
                )}
              </Stack>
              {c.url && (
                <IconButton size="small" component="a" href={c.url} target="_blank" rel="noreferrer">
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {c.threadTitle || t('earnedMedia.conversations.untitled')}
            </Typography>
            {c.excerpt && (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                « {c.excerpt} »
              </Typography>
            )}
            {c.suggestedAction && (
              <Box
                sx={{
                  mt: 1,
                  p: 1.5,
                  bgcolor: '#EFF6FF',
                  borderRadius: 1,
                  fontSize: 13,
                }}
              >
                💡 {c.suggestedAction}
              </Box>
            )}
          </Card>
        ))}
      </Stack>
    </Box>
  );
}

// ── Precision tab ──

function PrecisionTab({
  inaccuracies,
  recommendations,
  onCreateTask,
}: {
  inaccuracies: any[];
  recommendations: Recommendation[];
  onCreateTask?: (t: string, s?: 'high' | 'medium' | 'low') => void;
}) {
  const { t } = useTranslation();
  if (!inaccuracies.length && !recommendations.length) {
    return <EmptyState text={t('earnedMedia.precisionTab.empty')} />;
  }

  // Pair recommendations to inaccuracies by targetDomain when possible.
  const recsByDomain = new Map<string, Recommendation>();
  for (const r of recommendations) {
    if (r.targetDomain) recsByDomain.set(r.targetDomain, r);
  }

  return (
    <Box>
      <CalloutInfo>
        ⚠ {t('earnedMedia.precisionTab.callout')}
      </CalloutInfo>

      <Stack spacing={2} sx={{ mt: 2 }}>
        {inaccuracies.map((inacc, i) => {
          const matched = recsByDomain.get(inacc.domain || '');
          return (
            <PrecisionCard
              key={`${inacc.domain || 'i'}-${i}`}
              inacc={inacc}
              draftEmail={matched?.draftEmail}
              onCreateTask={onCreateTask}
            />
          );
        })}

        {/* Recommendations not paired to a specific inaccuracy */}
        {recommendations
          .filter((r) => !r.targetDomain || !inaccuracies.find((i) => i.domain === r.targetDomain))
          .map((r, i) => (
            <Card key={`rec-${i}`} variant="outlined" sx={{ p: 2 }}>
              <Typography variant="body2">{r.text}</Typography>
              {onCreateTask && (
                <Button
                  size="small"
                  startIcon={<AddTaskIcon />}
                  onClick={() => onCreateTask(r.text, r.severity)}
                  sx={{ mt: 1 }}
                >
                  {t('earnedMedia.createTask')}
                </Button>
              )}
            </Card>
          ))}
      </Stack>
    </Box>
  );
}

function PrecisionCard({
  inacc,
  draftEmail,
  onCreateTask,
}: {
  inacc: any;
  draftEmail?: string;
  onCreateTask?: (t: string, s?: 'high' | 'medium' | 'low') => void;
}) {
  const { t } = useTranslation();
  const [showDraft, setShowDraft] = useState(false);
  const [draft, setDraft] = useState(draftEmail || '');

  const copyDraft = async () => {
    try {
      await navigator.clipboard.writeText(draft);
    } catch {
      /* no-op */
    }
  };

  const severity = inacc.severity === 'critical' ? 'high' : 'medium';
  return (
    <Card
      variant="outlined"
      sx={{
        p: 2,
        borderLeft: 4,
        borderLeftColor: severity === 'high' ? '#EF4444' : '#F59E0B',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          📰 {inacc.sourceName || inacc.domain}
        </Typography>
        <Chip
          label={inacc.severity === 'critical' ? t('earnedMedia.severity.criticalFem') : t('earnedMedia.severity.minorFem')}
          size="small"
          sx={{
            bgcolor: severity === 'high' ? '#FEF2F2' : '#FFFBEB',
            color: severity === 'high' ? '#EF4444' : '#F59E0B',
            fontWeight: 600,
          }}
        />
      </Stack>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 70, pt: 0.5 }}>
          {t('earnedMedia.precisionTab.sourceSays')}
        </Typography>
        <Box
          sx={{
            flex: 1,
            p: 1,
            borderRadius: 1,
            bgcolor: '#FEF2F2',
            color: '#991B1B',
            fontSize: 13,
          }}
        >
          « {inacc.claim} »
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 70, pt: 0.5 }}>
          {t('earnedMedia.precisionTab.reality')}
        </Typography>
        <Box
          sx={{
            flex: 1,
            p: 1,
            borderRadius: 1,
            bgcolor: '#ECFDF5',
            color: '#065F46',
            fontSize: 13,
          }}
        >
          {inacc.reality}
        </Box>
      </Box>

      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        {draftEmail && (
          <Button size="small" onClick={() => setShowDraft((v) => !v)}>
            {showDraft ? t('earnedMedia.precisionTab.hideDraft') : t('earnedMedia.precisionTab.showDraft')}
          </Button>
        )}
        {onCreateTask && (
          <Button
            size="small"
            startIcon={<AddTaskIcon />}
            onClick={() =>
              onCreateTask(
                t('earnedMedia.correctInaccuracyTask', { source: inacc.sourceName || inacc.domain, claim: inacc.claim }),
                severity,
              )
            }
          >
            {t('earnedMedia.createTask')}
          </Button>
        )}
      </Stack>

      {showDraft && draftEmail && (
        <Box sx={{ mt: 2, p: 2, bgcolor: '#FAFBFC', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            {t('earnedMedia.precisionTab.draftCaption')}
          </Typography>
          <TextField
            multiline
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            fullWidth
            minRows={6}
            maxRows={14}
            sx={{ '& textarea': { fontFamily: 'monospace', fontSize: 12.5 } }}
          />
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button
              size="small"
              startIcon={<ContentCopyIcon fontSize="small" />}
              onClick={copyDraft}
            >
              {t('earnedMedia.precisionTab.copy')}
            </Button>
          </Stack>
        </Box>
      )}
    </Card>
  );
}

// ── Shared subcomponents ──

function KpiTile({
  label,
  value,
  hint,
  accent,
}: {
  label: string;
  value: string;
  hint?: string;
  accent?: string;
}) {
  return (
    <Card variant="outlined" sx={{ p: 1.5 }}>
      <Typography
        variant="caption"
        sx={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 0.4 }}
      >
        {label}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.25, color: accent || 'text.primary' }}>
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
          {hint}
        </Typography>
      )}
    </Card>
  );
}

/**
 * Wikipedia score explanation card.
 *
 * The Wikipedia quality score is graded against `BrandStrategy.envergure`,
 * not on a uniform rubric. A national SMB without a Wikipedia page can score
 * 70/100 because the Wikipedia notoriety bar excludes most SMBs by design —
 * recommending "create an article" would be misleading and would get rejected.
 * Users routinely confuse the score, so we explain it inline.
 *
 * Returns null when no Wikipedia data is available (legacy v1 audits or
 * pre-orchestrator runs).
 */
function WikipediaScoreCard({
  score,
  presence,
  envergure,
}: {
  score?: number;
  presence?: string;
  envergure?: string;
}) {
  const { t } = useTranslation();
  if (score === undefined || score === null) return null;

  const presenceLabelKeys: Record<string, string> = {
    own_page: 'earnedMedia.wikipedia.presence.own_page',
    mentioned_in_others: 'earnedMedia.wikipedia.presence.mentioned_in_others',
    wikidata_only: 'earnedMedia.wikipedia.presence.wikidata_only',
    absent: 'earnedMedia.wikipedia.presence.absent',
  };
  const envLabelKeys: Record<string, string> = {
    local: 'earnedMedia.wikipedia.env.local',
    regional: 'earnedMedia.wikipedia.env.regional',
    national: 'earnedMedia.wikipedia.env.national',
    international: 'earnedMedia.wikipedia.env.international',
    'hyper-local': 'earnedMedia.wikipedia.env.hyperLocal',
    'multi-local': 'earnedMedia.wikipedia.env.multiLocal',
    'mono-country': 'earnedMedia.wikipedia.env.national',
    'multi-country': 'earnedMedia.wikipedia.env.multiCountry',
    global: 'earnedMedia.wikipedia.env.global',
  };
  const envLabel = (e: string) => (envLabelKeys[e] ? t(envLabelKeys[e]) : e);
  const presenceLabel = (p: string) => (presenceLabelKeys[p] ? t(presenceLabelKeys[p]) : p);

  const env = (envergure || '').toLowerCase();
  const isLocalOrRegional = env === 'local' || env === 'regional' || env === 'hyper-local' || env === 'multi-local';
  const explanationLines: string[] = [];

  if (presence === 'absent' && isLocalOrRegional) {
    explanationLines.push(t('earnedMedia.wikipedia.explain.absentLocal', { env: envLabel(env) }));
    explanationLines.push(t('earnedMedia.wikipedia.explain.absentLocalAction'));
  } else if (presence === 'absent' && env === 'national') {
    explanationLines.push(t('earnedMedia.wikipedia.explain.absentNational'));
  } else if (presence === 'absent' && env === 'international') {
    explanationLines.push(t('earnedMedia.wikipedia.explain.absentInternational'));
  } else if (presence === 'own_page') {
    explanationLines.push(t('earnedMedia.wikipedia.explain.ownPage'));
  } else if (presence === 'mentioned_in_others') {
    explanationLines.push(t('earnedMedia.wikipedia.explain.mentionedInOthers'));
  } else if (presence === 'wikidata_only') {
    explanationLines.push(t('earnedMedia.wikipedia.explain.wikidataOnly'));
  }

  if (!explanationLines.length) {
    return null;
  }

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        bgcolor: '#FFFBEB',
        border: 1,
        borderColor: '#FDE68A',
        borderRadius: 1.5,
        fontSize: 13,
        color: '#78350F',
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {t('earnedMedia.wikipedia.scoreLine', { score, presence: presenceLabel(presence || '') })}
        {envergure && (
          <span style={{ fontWeight: 400, color: '#92400E' }}>
            {t('earnedMedia.wikipedia.envSuffix', { env: envLabel(env) })}
          </span>
        )}
      </Typography>
      {explanationLines.map((line, i) => (
        <Typography key={i} variant="body2" sx={{ mt: i === 0 ? 0.5 : 0.75, lineHeight: 1.5 }}>
          {line}
        </Typography>
      ))}
    </Box>
  );
}

function CalloutInfo({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        p: 2,
        bgcolor: '#EFF6FF',
        border: 1,
        borderColor: '#93C5FD',
        borderRadius: 1.5,
        fontSize: 13,
        color: '#1E3A8A',
      }}
    >
      {children}
    </Box>
  );
}

function EmptyState({ text, sub }: { text: string; sub?: string }) {
  return (
    <Box sx={{ p: 5, textAlign: 'center' }}>
      <LightbulbIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
        {text}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, maxWidth: 480, mx: 'auto' }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
}

function RecommendationList({
  title,
  recs,
  onCreateTask,
}: {
  title: string;
  recs: Recommendation[];
  onCreateTask?: (t: string, s?: 'high' | 'medium' | 'low') => void;
}) {
  const { t } = useTranslation();
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
        {title}
      </Typography>
      <Stack spacing={1}>
        {recs.map((r, i) => (
          <Card key={i} variant="outlined" sx={{ p: 1.5 }}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Chip
                label={SEVERITY_LABEL_KEYS[r.severity] ? t(SEVERITY_LABEL_KEYS[r.severity]) : r.severity}
                size="small"
                sx={{
                  bgcolor: `${SEVERITY_COLOR[r.severity] || '#6B7280'}20`,
                  color: SEVERITY_COLOR[r.severity] || '#6B7280',
                  fontWeight: 600,
                  fontSize: 11,
                  flexShrink: 0,
                }}
              />
              <Typography variant="body2" sx={{ flex: 1 }}>
                {r.text}
              </Typography>
              {onCreateTask && r.actionable && (
                <Tooltip title={t('earnedMedia.createTask')}>
                  <IconButton size="small" onClick={() => onCreateTask(r.text, r.severity)}>
                    <AddTaskIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Card>
        ))}
      </Stack>
    </Box>
  );
}
